// src/fleet-stats/analytics/buildInternalView.js
//
// The Internal lens answers a different question from the customer one.
//
// Fredrikstad is the warehouse where robots are built, set up and test-driven,
// and the wheelme sites are our own demo and production rigs. A robot there is
// not a fleet asset being under-used — it is a unit passing through. Across the
// whole internal estate 99 robots have driven 2.6 km between them, and the
// median robot is present on two days. Utilisation, anomalies and offline flags
// are all meaningless against that shape: idle IS the normal end state.
//
// What does carry signal is throughput — how many robots arrived this month,
// what is on the floor right now, how long units sit, and where they went. That
// last one needs rows from OUTSIDE the internal audience, which is why this
// takes both the full row set and the internal subset.

import { addDays } from "../utils/date.js";
import { INACTIVE_CUTOFF_DAYS } from "../constants.js";
import { classifyAudience } from "../utils/audience.js";

function monthKey(iso) {
    return (iso || "").slice(0, 7);
}

/**
 * @param allRows   every flattened row, all audiences — needed to trace where a
 *                  robot went after it left the warehouse
 * @param maxDate   latest date in the dataset
 */
export function buildInternalView(allRows, maxDate) {
    if (!maxDate || !allRows || allRows.length === 0) return null;

    const internalRows = allRows.filter(
        (r) => classifyAudience(r.client, r.siteName) === "internal"
    );
    if (internalRows.length === 0) return null;

    // ── Per-robot footprint inside the internal estate ────────────────────────
    const byRobot = new Map();
    for (const r of internalRows) {
        let e = byRobot.get(r.robotId);
        if (!e) {
            e = {
                robotId: r.robotId,
                robotName: r.robotName || r.robotId,
                siteName: r.siteName,
                // Both fredrikstad and wheelme have a site called "Production",
                // so the bare site name cannot tell the warehouse from our own
                // production rig — which is exactly what this table is for.
                client: r.client,
                firstSeen: r.date,
                lastSeen: r.date,
                dates: new Set(),
                uptime_s: 0,
                distance_m: 0,
            };
            byRobot.set(r.robotId, e);
        }
        if (r.date < e.firstSeen) e.firstSeen = r.date;
        if (r.date > e.lastSeen) e.lastSeen = r.date;
        e.dates.add(r.date);
        e.uptime_s += r.uptime_s || 0;
        e.distance_m += r.distance_m || 0;
    }

    // ── Where each robot ended up ────────────────────────────────────────────
    // "Now at" is the non-internal site where the robot was seen most recently.
    // Not "the site it shipped to": units come back for repair and go out again,
    // so a first-hop model would report a stale destination for those. Where it
    // was last seen answers the question people actually ask.
    const destination = new Map();
    for (const r of allRows) {
        if (!byRobot.has(r.robotId)) continue;
        if (classifyAudience(r.client, r.siteName) === "internal") continue;
        const prev = destination.get(r.robotId);
        if (!prev || r.date > prev.date) {
            destination.set(r.robotId, { site: r.siteName, client: r.client, date: r.date });
        }
    }

    const lifecycle = Array.from(byRobot.values()).map((e) => {
        const dest = destination.get(e.robotId) || null;
        return {
            robotId: e.robotId,
            robotName: e.robotName,
            siteName: e.siteName,
            client: e.client,
            siteLabel: e.client ? `${e.client} · ${e.siteName}` : e.siteName,
            firstSeen: e.firstSeen,
            lastSeen: e.lastSeen,
            days: e.dates.size,
            testHours: e.uptime_s / 3600,
            km: e.distance_m / 1000,
            destination: dest ? dest.site : null,
            destinationClient: dest ? dest.client : null,
            destinationDate: dest ? dest.date : null,
        };
    });
    lifecycle.sort((a, b) => (a.lastSeen < b.lastSeen ? 1 : a.lastSeen > b.lastSeen ? -1 : 0));

    // ── Commissioning volume ─────────────────────────────────────────────────
    // A robot is counted in the month it was FIRST seen anywhere internal, so a
    // unit that comes back for repair is not counted twice.
    const perMonth = new Map();
    for (const e of byRobot.values()) {
        const key = monthKey(e.firstSeen);
        perMonth.set(key, (perMonth.get(key) || 0) + 1);
    }
    const commissioned = Array.from(perMonth.entries())
        .map(([month, count]) => ({ month, count }))
        .sort((a, b) => (a.month < b.month ? -1 : 1));

    const floorCutoff = addDays(maxDate, -(INACTIVE_CUTOFF_DAYS - 1));
    const onFloor = lifecycle.filter((e) => e.lastSeen >= floorCutoff);
    const shipped = lifecycle.filter((e) => e.destination);

    return {
        maxDate,
        totalRobots: lifecycle.length,
        testHours: lifecycle.reduce((s, e) => s + e.testHours, 0),
        km: lifecycle.reduce((s, e) => s + e.km, 0),
        onFloor,
        shipped,
        commissioned,
        thisMonth: perMonth.get(monthKey(maxDate)) || 0,
        lifecycle,
    };
}
